import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';

import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import WideLayout from '@components/layout/wide';
import { MissingPropsRecovery } from '@components/missing-props-recovery';
import { PlayerProfileView } from '@components/player-profile/profile-view';
import type { Breadcrumb } from '@components/player-profile/hero';

import { FFH_HOSTNAME } from '@lib/constants';
import { fetchLeagueContext } from '@api-service/league-context';
import {
  locatePlayerBySlugSearch,
  resolvePlayerPage
} from '@lib/page-utils/player-profile/catalogue';
import {
  buildBreadcrumbJsonLd,
  buildPlayerBreadcrumbs,
  buildPlayerDescription,
  buildPlayerPersonJsonLd,
  buildPlayerTitle
} from '@lib/page-utils/player-profile/seo';
import {
  PLAYERS_INDEX_PATH,
  toPlayerProfilePath
} from '@lib/page-utils/player-profile/slugs';
import type { Player as FfhPlayer } from '@api-service/players';

const BRAND_NAME = 'Fantasy Football Hub';
const PROFILE_REVALIDATE_SECONDS = 3600;
/**
 * Bounded negative caching: a bad slug 404s, but the miss is re-checked
 * hourly so a player added mid-season (or a renamed slug) recovers without a
 * deploy.
 */
const NOT_FOUND_REVALIDATE_SECONDS = 3600;

type PlayerPageProps = {
  /**
   * Always supplied by getStaticProps, but a client-side navigation can
   * render the page without it when the props fetch fails (a deploy
   * invalidating the open tab's _next/data URLs is the known vector), so
   * the component must tolerate its absence. See MissingPropsRecovery.
   */
  player?: FfhPlayer;
  clubSlug: string;
  playerSlug: string;
  /** The club's display name from the teams list, e.g. "Man City". */
  teamName: string;
  /** Gates the season-total sentence in the meta description. */
  isSeasonUnderway: boolean;
};

/**
 * One reload per URL per tab: enough to recover from deploy skew without
 * ever looping on a URL that is broken for a real reason.
 */
const PROPS_RELOAD_MARKER_KEY = 'ffh-player-page-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'player-page-missing-props';

const PlayerPage = ({
  player,
  clubSlug,
  playerSlug,
  teamName,
  isSeasonUnderway
}: PlayerPageProps) => {
  const { isAppView } = useIsAppView();
  const isPlayerProfilePageClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.playerProfilePageIsClosed,
    false
  );

  const optaId = player?.externalIds?.optaUuid ?? null;
  if (!player || optaId === null) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={PLAYERS_INDEX_PATH}
        errorMessage="Player page props missing"
      />
    );
  }

  const canonicalUrl = `${FFH_HOSTNAME}${toPlayerProfilePath(
    clubSlug,
    playerSlug
  )}`;
  const title = buildPlayerTitle(player);
  const description = buildPlayerDescription(player, { isSeasonUnderway });

  const breadcrumbEntries = buildPlayerBreadcrumbs(
    teamName,
    clubSlug,
    player.displayName
  );
  const heroBreadcrumbs: Breadcrumb[] = breadcrumbEntries.map((entry) => ({
    label: entry.label,
    ...(entry.path ? { href: entry.path } : {})
  }));

  const head = (
    <Head>
      <title>{`${title} | ${BRAND_NAME}`}</title>
      <meta name="description" content={description} key="description" />
      {/* Season and filter views travel as query parameters, so every
          variant canonicalises back to this base URL. */}
      <link rel="canonical" href={canonicalUrl} key="canonical" />
      <meta property="og:type" content="profile" key="og:type" />
      <meta property="og:title" content={title} key="og:title" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      <meta property="og:url" content={canonicalUrl} key="og:url" />
      <meta property="og:site_name" content={BRAND_NAME} key="og:site_name" />
      {player.imageUrl && (
        <meta property="og:image" content={player.imageUrl} key="og:image" />
      )}
      <meta name="twitter:card" content="summary" key="twitter:card" />
      <meta name="twitter:title" content={title} key="twitter:title" />
      <meta
        name="twitter:description"
        content={description}
        key="twitter:description"
      />
      <script
        type="application/ld+json"
        key="jsonld-person"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            buildPlayerPersonJsonLd(player, clubSlug, playerSlug)
          )
        }}
      />
      <script
        type="application/ld+json"
        key="jsonld-breadcrumbs"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildBreadcrumbJsonLd(breadcrumbEntries))
        }}
      />
    </Head>
  );

  if (isPlayerProfilePageClosed) {
    return (
      <>
        {head}
        <WideLayout>
          <div className="bg-gray-50 px-2 pt-2 pb-10 sm:px-4 lg:pt-8">
            <EndOfSeasonHoldingMessage
              pageTitle="The Player Profile page"
              isAppView={isAppView}
            />
          </div>
        </WideLayout>
      </>
    );
  }

  return (
    <>
      {head}
      <PlayerProfileView
        optaId={optaId}
        staticPlayer={player}
        breadcrumbs={heroBreadcrumbs}
        hasOwnHead
      />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  // ~700 players regenerate on demand rather than at build time; the sitemap
  // hands crawlers the full URL set.
  paths: [],
  fallback: 'blocking'
});

export const getStaticProps: GetStaticProps<PlayerPageProps> = async ({
  params
}) => {
  const clubSlug = typeof params?.club === 'string' ? params.club : null;
  const playerSlug = typeof params?.player === 'string' ? params.player : null;

  if (!clubSlug || !playerSlug) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  const [resolved, leagueContext] = await Promise.all([
    resolvePlayerPage(clubSlug, playerSlug),
    // The description only asserts season totals when the season is
    // confirmed to be underway, so a failed context fetch degrades to the
    // identity-only wording rather than failing the page.
    fetchLeagueContext().catch(() => null)
  ]);
  if (!resolved) {
    // The club segment no longer matches: a transfer, a relegated club, or a
    // mislinked club name. If the slug still identifies exactly one player
    // in the league, the URL follows them to their current club; only then
    // does it 404.
    const relocated = await locatePlayerBySlugSearch(playerSlug);
    if (relocated) {
      return {
        redirect: {
          destination: toPlayerProfilePath(
            relocated.clubSlug,
            relocated.playerSlug
          ),
          permanent: true
        },
        revalidate: PROFILE_REVALIDATE_SECONDS
      };
    }
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  // The resolver accepts historical slug variants (a suffix left over from a
  // departed namesake) but reports the current canonical slug. Serving the
  // variant would leave two live URLs for one player, so it redirects.
  if (resolved.playerSlug !== playerSlug) {
    return {
      redirect: {
        destination: toPlayerProfilePath(
          resolved.clubSlug,
          resolved.playerSlug
        ),
        permanent: true
      },
      revalidate: PROFILE_REVALIDATE_SECONDS
    };
  }

  const isSeasonUnderway =
    leagueContext != null && leagueContext.seasonStatus !== 'PRE_SEASON';

  return {
    props: {
      player: resolved.player,
      clubSlug: resolved.clubSlug,
      playerSlug: resolved.playerSlug,
      teamName: resolved.team.name,
      isSeasonUnderway
    },
    revalidate: PROFILE_REVALIDATE_SECONDS
  };
};

export default PlayerPage;
